import { jsPDF } from "jspdf";
import logoTamep from "@/assets/img/fondotam-negro.png";

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE PÁGINA A4 Y ETIQUETA
// ─────────────────────────────────────────────────────────────────────────────
export const CONFIG = {
  pageW: 210,
  pageH: 297,

  labelW: 50,   // mm — ancho de etiqueta
  labelH: 25,   // mm — alto de etiqueta

  marginLeft: 2,
  marginTop: 8,

  gapX: 2,   // 4 cols × 50mm + 3 gaps × 2mm + 2 márgenes × 2mm = 210mm
  gapY: 3,
};

CONFIG.cols = 4;
CONFIG.rows = Math.floor(
  (CONFIG.pageH - CONFIG.marginTop * 2 + CONFIG.gapY) / (CONFIG.labelH + CONFIG.gapY)
);
CONFIG.totalSlots = CONFIG.cols * CONFIG.rows;

// ─────────────────────────────────────────────────────────────────────────────
// PRECARGA DE IMAGEN
// ─────────────────────────────────────────────────────────────────────────────
export const precargarImagen = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    if (typeof Image === "undefined") return resolve(url);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// DIBUJA UNA ETIQUETA DE 50×25 mm
// ─────────────────────────────────────────────────────────────────────────────
export const dibujarEtiqueta = (doc, activo, lx = 0, ly = 0, esTermica = false) => {
  // ── Dibujar encuadre/margen ──
  doc.setLineWidth(esTermica ? 0.3 : 0.5);
  doc.setDrawColor(0, 0, 0);

  if (esTermica) {
    // 0.25mm de margen interior para proteger contra tolerancias de arrastre
    doc.rect(lx + 0.25, ly + 0.25, CONFIG.labelW - 0.5, CONFIG.labelH - 0.5);
  } else {
    doc.rect(lx, ly, CONFIG.labelW, CONFIG.labelH);
  }

  // ── Logo ──
  try {
    doc.addImage(logoTamep, "PNG", lx + 1, ly + 0.9, 13, 8);
  } catch (_) { /* logo opcional */ }

  // ── Nombre institución ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(3.7);
  doc.text("EP TRANSPORTE AÉREO MILITAR", lx + 1, ly + 10.3);

  // ── Línea separadora ──
  doc.setLineWidth(0.25);
  doc.line(lx + 1, ly + 11.3, lx + 28, ly + 11.3);

  // ── Código QR ──
  const qrSource = activo.qrElement || activo.qrUrl;
  if (qrSource) {
    try {
      doc.addImage(qrSource, "PNG", lx + 29.5, ly + 1.8, 19.5, 19.5);
    } catch (_) { /* QR opcional */ }
  }

  // ── Código TAMEP ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text(`${activo.codigo_tamep || "S/C"}`, lx + 1, ly + 16);

  // ── Código TAB histórico ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text(`${activo.codigo_tab || "S/C"}`, lx + 1, ly + 20);
};

// ─────────────────────────────────────────────────────────────────────────────
// DISPARAR IMPRESIÓN DEL PDF
// ─────────────────────────────────────────────────────────────────────────────
const abrirPdf = async (doc) => {
  doc.autoPrint();
  const pdfUrl = doc.output("bloburl");
  const nuevaVentana = window.open(pdfUrl, "_blank");

  if (!nuevaVentana || nuevaVentana.closed || typeof nuevaVentana.closed === "undefined") {
    const { default: Swal } = await import("sweetalert2");
    Swal.fire({
      title: "Ventana Bloqueada",
      text: "Tu navegador bloqueó el PDF. Por favor, permite las ventanas emergentes para SIGAF.",
      icon: "warning",
      confirmButtonText: "Entendido",
    });
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL MINIMALISTA: SELECCIÓN DEL MÉTODO DE IMPRESIÓN
// ─────────────────────────────────────────────────────────────────────────────
export const mostrarModalSeleccionMetodo = (activo) => {
  return new Promise((resolve) => {
    const prevModal = document.getElementById("tamep-metodo-modal");
    if (prevModal) prevModal.remove();

    const ultimoMetodo = localStorage.getItem("tamep_pref_metodo_impresion") || "termica";

    const overlay = document.createElement("div");
    overlay.id = "tamep-metodo-modal";
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 99999;
      background: rgba(15, 23, 42, 0.55);
      display: flex; align-items: center; justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      padding: 16px;
    `;

    overlay.innerHTML = `
      <div style="
        background: #ffffff;
        border-radius: 10px;
        width: 100%;
        max-width: 480px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        border: 1px solid #e2e8f0;
      ">
        <!-- Encabezado simple -->
        <div style="
          padding: 14px 18px;
          background: #003366;
          color: #ffffff;
          display: flex; align-items: center; justify-content: space-between;
        ">
          <div>
            <h3 style="margin: 0; font-size: 14px; font-weight: 600; letter-spacing: 0.3px;">
              Formato de Impresión
            </h3>
            <p style="margin: 2px 0 0; font-size: 11px; opacity: 0.85;">
              Activo: ${activo.codigo_tamep || activo.codigo_tab || 'Sin código'}
            </p>
          </div>
          <button id="tamep-metodo-close" style="
            background: transparent; border: none; color: #ffffff;
            font-size: 20px; line-height: 1; cursor: pointer; padding: 2px 6px; opacity: 0.8;
          " title="Cerrar">&times;</button>
        </div>

        <!-- Opciones minimalistas -->
        <div style="padding: 16px 18px;">
          <p style="margin: 0 0 12px 0; font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
            Selecciona la salida:
          </p>

          <div style="display: flex; flex-direction: column; gap: 8px;">
            
            <!-- Opción 1: Térmica 50x25 mm -->
            <div id="btn-opcion-termica" class="tamep-opt-item ${ultimoMetodo === 'termica' ? 'activo' : ''}" style="
              border: 1px solid ${ultimoMetodo === 'termica' ? '#003366' : '#cbd5e1'};
              background: ${ultimoMetodo === 'termica' ? '#f0f7ff' : '#ffffff'};
              border-radius: 8px;
              padding: 12px 14px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: space-between;
            ">
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="font-size: 20px;">🏷️</span>
                <div>
                  <strong style="color: #0f172a; font-size: 13px; display: block;">Impresora Térmica</strong>
                  <span style="color: #64748b; font-size: 11px; display: block; margin-top: 1px;">
                    Rollo continuo 50×25 mm (Zebra, Xprinter, TSC)
                  </span>
                </div>
              </div>
              <span style="
                font-size: 10px; font-weight: 700; color: #003366;
                background: #e0f2fe; padding: 3px 8px; border-radius: 4px;
              ">50×25 mm</span>
            </div>

            <!-- Opción 2: Hoja A4 -->
            <div id="btn-opcion-a4" class="tamep-opt-item ${ultimoMetodo === 'a4' ? 'activo' : ''}" style="
              border: 1px solid ${ultimoMetodo === 'a4' ? '#003366' : '#cbd5e1'};
              background: ${ultimoMetodo === 'a4' ? '#f0f7ff' : '#ffffff'};
              border-radius: 8px;
              padding: 12px 14px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: space-between;
            ">
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="font-size: 20px;">📄</span>
                <div>
                  <strong style="color: #0f172a; font-size: 13px; display: block;">Hoja A4 Adhesiva</strong>
                  <span style="color: #64748b; font-size: 11px; display: block; margin-top: 1px;">
                    Elegir posición en grilla 4×11 (Epson, Láser)
                  </span>
                </div>
              </div>
              <span style="
                font-size: 10px; font-weight: 700; color: #475569;
                background: #f1f5f9; padding: 3px 8px; border-radius: 4px;
              ">Grilla A4</span>
            </div>

          </div>
        </div>

        <!-- Pie sobrio -->
        <div style="
          padding: 10px 18px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          display: flex; align-items: center; justify-content: space-between;
        ">
          <small style="color: #94a3b8; font-size: 11px;">
            Atajos: [1] Térmica &bull; [2] A4 &bull; [Esc] Cancelar
          </small>
          <button id="tamep-metodo-cancel" style="
            background: #ffffff; border: 1px solid #cbd5e1; border-radius: 6px;
            padding: 5px 12px; font-size: 12px; color: #475569; cursor: pointer;
          ">Cancelar</button>
        </div>
      </div>

      <style>
        .tamep-opt-item {
          transition: border-color 0.15s, background-color 0.15s;
        }
        .tamep-opt-item:hover {
          border-color: #003366 !important;
          background-color: #f8fafc !important;
        }
        #tamep-metodo-close:hover {
          opacity: 1 !important;
        }
        #tamep-metodo-cancel:hover {
          background-color: #f1f5f9;
          color: #0f172a;
        }
      </style>
    `;

    document.body.appendChild(overlay);

    const limpiar = (resultado) => {
      window.removeEventListener("keydown", handleKey);
      overlay.remove();
      resolve(resultado);
    };

    const handleKey = (e) => {
      if (e.key === "Escape") limpiar(null);
      if (e.key === "1") {
        localStorage.setItem("tamep_pref_metodo_impresion", "termica");
        limpiar("termica");
      }
      if (e.key === "2") {
        localStorage.setItem("tamep_pref_metodo_impresion", "a4");
        limpiar("a4");
      }
    };
    window.addEventListener("keydown", handleKey);

    document.getElementById("btn-opcion-termica").addEventListener("click", () => {
      localStorage.setItem("tamep_pref_metodo_impresion", "termica");
      limpiar("termica");
    });

    document.getElementById("btn-opcion-a4").addEventListener("click", () => {
      localStorage.setItem("tamep_pref_metodo_impresion", "a4");
      limpiar("a4");
    });

    document.getElementById("tamep-metodo-close").addEventListener("click", () => limpiar(null));
    document.getElementById("tamep-metodo-cancel").addEventListener("click", () => limpiar(null));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) limpiar(null);
    });
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE SELECCIÓN DE POSICIÓN EN HOJA A4 (MINIMALISTA)
// ─────────────────────────────────────────────────────────────────────────────
export const mostrarSelectorPosicion = (activo) => {
  return new Promise((resolve) => {
    const prevModal = document.getElementById("tamep-pos-modal");
    if (prevModal) prevModal.remove();

    const { cols, rows, totalSlots } = CONFIG;

    const overlay = document.createElement("div");
    overlay.id = "tamep-pos-modal";
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 99999;
      background: rgba(15, 23, 42, 0.55);
      display: flex; align-items: center; justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      padding: 16px;
    `;

    let selectedSlot = null;
    let selectedEl = null;

    const storageKey = "tamep_hoja_slots";
    let usedSlots = [];
    try {
      usedSlots = JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch (_) { /* no-op */ }

    if (usedSlots.length >= totalSlots) {
      usedSlots = [];
      try { localStorage.removeItem(storageKey); } catch (_) { /* no-op */ }
    }

    const gridCells = Array.from({ length: rows }, (_, r) =>
      Array.from({ length: cols }, (_, c) => {
        const slot = r * cols + c;
        const isUsed = usedSlots.includes(slot);
        const title = isUsed
          ? "Ya impresa"
          : `Posición ${slot + 1} (Fila ${r + 1}, Columna ${c + 1})`;
        return `
          <div class="tamep-cell ${isUsed ? "used" : "free"}"
               data-slot="${slot}" data-col="${c}" data-row="${r}"
               title="${title}">
            <span class="cell-num">${slot + 1}</span>
          </div>`;
      }).join("")
    ).join("");

    overlay.innerHTML = `
      <div style="
        background: #ffffff; border-radius: 10px; width: 95vw; max-width: 650px;
        max-height: 90vh; overflow-y: auto;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
        border: 1px solid #e2e8f0;
      ">
        <!-- Encabezado -->
        <div style="
          background: #003366;
          padding: 14px 18px; color: #ffffff;
          display: flex; align-items: center; justify-content: space-between;
        ">
          <div>
            <h3 style="margin: 0; font-size: 14px; font-weight: 600; letter-spacing: 0.3px;">
              Posición en Hoja A4 (50×25 mm)
            </h3>
            <p style="margin: 2px 0 0; font-size: 11px; opacity: 0.85;">
              ${cols} columnas &times; ${rows} filas &bull; ${totalSlots} posiciones en la hoja
            </p>
          </div>
          <button id="tamep-close-btn" style="
            background: transparent; border: none; color: #ffffff;
            font-size: 20px; line-height: 1; cursor: pointer; padding: 2px 6px; opacity: 0.8;
          " title="Cancelar">&times;</button>
        </div>

        <!-- Leyenda -->
        <div style="
          padding: 10px 18px;
          display: flex; gap: 14px; flex-wrap: wrap; align-items: center;
          border-bottom: 1px solid #f1f5f9; background: #f8fafc;
        ">
          <span style="display:flex;align-items:center;gap:5px;font-size:11px;color:#475569;">
            <span style="width:12px;height:12px;border-radius:3px;background:#e8f4f8;border:1px solid #003366;"></span>Libre
          </span>
          <span style="display:flex;align-items:center;gap:5px;font-size:11px;color:#475569;">
            <span style="width:12px;height:12px;border-radius:3px;background:#fde8e8;border:1px solid #dc2626;"></span>Usada
          </span>
          <span style="display:flex;align-items:center;gap:5px;font-size:11px;color:#475569;">
            <span style="width:12px;height:12px;border-radius:3px;background:#003366;border:1px solid #001d3d;"></span>Seleccionada
          </span>
          <button id="tamep-clear-btn" style="
            margin-left: auto; background: #ffffff; border: 1px solid #d1d5db;
            border-radius: 6px; padding: 4px 10px; font-size: 11px; color: #64748b;
            cursor: pointer;
          ">Reiniciar hoja</button>
        </div>

        <!-- Grilla -->
        <div style="padding: 14px 18px;">
          <div id="tamep-grid" style="
            display: grid;
            grid-template-columns: repeat(${cols}, 1fr);
            gap: 6px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 10px;
          ">${gridCells}</div>
        </div>

        <!-- Pie de acción -->
        <div style="
          padding: 12px 18px;
          border-top: 1px solid #e2e8f0;
          display: flex; align-items: center; justify-content: space-between;
          background: #ffffff;
        ">
          <div id="tamep-info" style="font-size: 12px; color: #64748b;">
            Haz clic en una casilla libre para seleccionarla
          </div>
          <div style="display: flex; gap: 8px;">
            <button id="tamep-cancel-btn" style="
              background: #ffffff; border: 1px solid #cbd5e1; border-radius: 6px;
              padding: 7px 14px; font-size: 12px; color: #475569; cursor: pointer;
            ">Cancelar</button>
            <button id="tamep-print-btn" disabled style="
              background: #cbd5e1; border: none; border-radius: 6px;
              padding: 7px 16px; font-size: 12px; cursor: not-allowed;
              color: #ffffff; font-weight: 600;
            ">Imprimir en posición</button>
          </div>
        </div>
      </div>

      <style>
        .tamep-cell {
          border-radius: 6px; padding: 6px 2px; text-align: center;
          cursor: pointer; min-height: 34px;
          display: flex; align-items: center; justify-content: center;
          user-select: none; font-size: 11px; font-weight: 600;
          transition: background-color 0.1s, border-color 0.1s;
        }
        .tamep-cell.free {
          background: #ffffff; border: 1px solid #cbd5e1; color: #003366;
        }
        .tamep-cell.free:hover {
          background: #e0f2fe; border-color: #003366;
        }
        .tamep-cell.used {
          background: #fef2f2; border: 1px solid #fecaca; color: #dc2626;
          cursor: not-allowed; opacity: 0.6;
        }
        .tamep-cell.selected {
          background: #003366 !important; border-color: #003366 !important;
          color: #ffffff !important;
        }
        #tamep-cancel-btn:hover, #tamep-clear-btn:hover {
          background: #f1f5f9;
        }
        #tamep-close-btn:hover {
          opacity: 1 !important;
        }
      </style>
    `;

    document.body.appendChild(overlay);

    const printBtn = document.getElementById("tamep-print-btn");
    const infoDiv = document.getElementById("tamep-info");
    const grid = document.getElementById("tamep-grid");

    grid.addEventListener("click", (e) => {
      const cell = e.target.closest(".tamep-cell");
      if (!cell || cell.classList.contains("used")) return;

      if (selectedEl) selectedEl.classList.remove("selected");
      cell.classList.add("selected");
      selectedEl = cell;
      selectedSlot = parseInt(cell.dataset.slot);

      const col = parseInt(cell.dataset.col);
      const row = parseInt(cell.dataset.row);
      infoDiv.innerHTML = `Posición <strong>${selectedSlot + 1}</strong> seleccionada (Fila ${row + 1}, Col. ${col + 1})`;
      infoDiv.style.color = "#003366";

      printBtn.disabled = false;
      printBtn.style.cursor = "pointer";
      printBtn.style.background = "#003366";
    });

    document.getElementById("tamep-clear-btn").addEventListener("click", () => {
      localStorage.removeItem(storageKey);
      overlay.remove();
      setTimeout(() => mostrarSelectorPosicion(activo).then(resolve), 50);
    });

    const cerrar = () => { overlay.remove(); resolve(null); };
    document.getElementById("tamep-cancel-btn").addEventListener("click", cerrar);
    document.getElementById("tamep-close-btn").addEventListener("click", cerrar);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cerrar(); });

    printBtn.addEventListener("click", () => {
      if (selectedSlot === null) return;
      overlay.remove();

      usedSlots.push(selectedSlot);
      try {
        localStorage.setItem(storageKey, JSON.stringify(usedSlots));
      } catch (_) { /* no-op */ }

      resolve({
        col: selectedSlot % CONFIG.cols,
        row: Math.floor(selectedSlot / CONFIG.cols),
      });
    });
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// IMPRESIÓN DIRECTA EN IMPRESORA TÉRMICA (50×25 mm)
// ─────────────────────────────────────────────────────────────────────────────
export const imprimirEtiquetaTermicaDirecta = async (activo) => {
  const qrUrl = activo.qrUrl || activo.imagen_qr;
  const qrImg = await precargarImagen(qrUrl);
  const activoPreparado = { ...activo, qrElement: qrImg };

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [25, 50],
  });

  dibujarEtiqueta(doc, activoPreparado, 0, 0, true);
  await abrirPdf(doc);
};

// ─────────────────────────────────────────────────────────────────────────────
// IMPRESIÓN EN HOJA A4 CON SELECCIÓN DE POSICIÓN
// ─────────────────────────────────────────────────────────────────────────────
export const imprimirEtiquetaA4ConPosicion = async (activo) => {
  const posicion = await mostrarSelectorPosicion(activo);
  if (!posicion) return;

  const qrUrl = activo.qrUrl || activo.imagen_qr;
  const qrImg = await precargarImagen(qrUrl);
  const activoPreparado = { ...activo, qrElement: qrImg };

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const lx = CONFIG.marginLeft + posicion.col * (CONFIG.labelW + CONFIG.gapX);
  const ly = CONFIG.marginTop + posicion.row * (CONFIG.labelH + CONFIG.gapY);

  dibujarEtiqueta(doc, activoPreparado, lx, ly, false);
  await abrirPdf(doc);
};

// ─────────────────────────────────────────────────────────────────────────────
// FUNCIÓN PRINCIPAL EXPORTADA
// ─────────────────────────────────────────────────────────────────────────────
export const generarEtiquetaTermica = async (activo) => {
  const qrUrl = activo?.qrUrl || activo?.imagen_qr;
  if (!activo || !qrUrl) {
    const { default: Swal } = await import("sweetalert2");
    return Swal.fire("Sin datos", "Faltan datos o el código QR para imprimir.", "warning");
  }

  const modo = await mostrarModalSeleccionMetodo(activo);
  if (!modo) return;

  if (modo === "termica") {
    await imprimirEtiquetaTermicaDirecta(activo);
  } else if (modo === "a4") {
    await imprimirEtiquetaA4ConPosicion(activo);
  }
};